
import React from "react";
import { CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Small } from "@/components/ui/typography";
import AuthDivider from "./AuthDivider";
import SocialLoginButton from "./SocialLoginButton";
import AuthButton from "./AuthButton";
import { useNavigate } from "react-router-dom";
import { useAuthForm } from "@/context/AuthFormContext";

// Create a Label component that uses Typography internally
const Label = ({ htmlFor, children }: { htmlFor: string, children: React.ReactNode }) => (
  <Small weight="medium" className="text-mono-seasalt block mb-1.5">
    {children}
  </Small>
);

type LoginFormProps = {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  loading: boolean;
  googleLoading: boolean;
  handleLogin: (e: React.FormEvent) => Promise<void>;
  handleGoogleLogin: () => Promise<void>;
};

const LoginForm = ({
  email,
  setEmail,
  password,
  setPassword,
  loading,
  googleLoading,
  handleLogin,
  handleGoogleLogin
}: LoginFormProps) => {
  const navigate = useNavigate();
  const { showPassword, setShowPassword, setMode } = useAuthForm();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      return;
    }
    
    try {
      await handleLogin(e);
    } catch (error) {
      console.error("Login error:", error);
    }
  };

  const handleForgotPasswordClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // First set the mode to immediately hide the current form
    setMode("forgotPassword");
    // Then navigate to maintain routing history
    navigate("/auth?mode=forgotPassword");
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col">
      <CardContent className="space-y-4 pt-4 pb-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@gmail.com"
            required
            className="bg-mono-onyx border-mono-slateGray/30 transition-colors"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <PasswordInput
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            className="bg-mono-onyx border-mono-slateGray/30 transition-colors"
            showPasswordState={[showPassword, setShowPassword]}
          />
          <div className="flex justify-start h-6">
            <Small 
              onClick={handleForgotPasswordClick}
              className="text-mono-frenchGray hover:text-mono-seasalt transition-colors text-xs cursor-pointer"
            >
              Forgot password?
            </Small>
          </div>
        </div>
      </CardContent>
      
      <CardFooter className="flex flex-col gap-4 pt-4 pb-8">
        <AuthButton 
          type="submit" 
          loading={loading}
        >
          {loading ? "Logging in..." : "Login"}
        </AuthButton>
        
        <AuthDivider />
        
        <SocialLoginButton 
          provider="google" 
          onClick={handleGoogleLogin} 
          loading={googleLoading} 
        />
      </CardFooter>
    </form>
  );
};

export default LoginForm;
